import React from 'react';

const fill = (text, fallback) => (text && String(text).trim() ? text : fallback);

const parseDate = (iso) => {
  if (!iso) return null;
  const raw = String(iso);
  if (/[A-Za-z]/.test(raw) && !raw.includes('T')) return null;
  const d = new Date(raw.includes('T') ? raw : `${raw}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const prettyDate = (iso) => {
  if (!iso) return '—';
  const d = parseDate(iso);
  if (!d) return String(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

const dmy = (iso) => {
  if (!iso) return '—';
  const d = parseDate(iso);
  if (!d) return String(iso);
  return d.toLocaleDateString('en-GB');
};

const roleShort = (title) => String(title || '').replace(/\s+intern$/i, '').trim() || title;

const RichLine = ({ text }) => {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i} className="font-bold text-[#0f172a]">{part.slice(2, -2)}</strong>;
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
};

/* Accurate Growmore Logo Lockup (HD Original Typography & Graphics) */
const GrowmoreLogo = ({ className = '', size = 'md' }) => {
  const isLarge = size === 'lg';
  return (
    <div className={`inline-flex items-center justify-center select-none ${className}`}>
      <img
        src="/logo-full.png"
        alt="Growmore IT Services"
        className={`${isLarge ? 'h-[72px]' : 'h-[56px]'} w-auto object-contain shrink-0`}
        draggable="false"
      />
    </div>
  );
};

/* Renders custom uploaded SVG or Image data */
const SvgOrImage = ({ content, className = '', fallback = null }) => {
  if (!content) return fallback;
  const trimmed = String(content).trim();
  if (trimmed.startsWith('<svg')) {
    return (
      <div
        className={`inline-flex items-center justify-center ${className}`}
        dangerouslySetInnerHTML={{ __html: trimmed }}
      />
    );
  }
  return <img src={trimmed} alt="" className={`object-contain ${className}`} />;
};

const GlobeIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const MailIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M22 6l-10 7L2 6" />
  </svg>
);

/* Clean subtle diagonal watermark for Offer Letter */
const CleanOfferWatermark = () => (
  <svg
    className="absolute inset-0 w-full h-full pointer-events-none select-none opacity-40"
    viewBox="0 0 794 1123"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    <defs>
      <pattern
        id="clean-offer-stripes"
        width="14"
        height="14"
        patternTransform="rotate(45 0 0)"
        patternUnits="userSpaceOnUse"
      >
        <line x1="0" y1="0" x2="0" y2="14" stroke="#cbd5e1" strokeWidth="1.5" />
      </pattern>
      <clipPath id="tl-clip">
        <polygon points="0,0 260,0 0,260" />
      </clipPath>
      <clipPath id="br-clip">
        <polygon points="794,1123 534,1123 794,863" />
      </clipPath>
    </defs>
    <rect x="0" y="0" width="260" height="260" fill="url(#clean-offer-stripes)" clipPath="url(#tl-clip)" />
    <rect x="534" y="863" width="260" height="260" fill="url(#clean-offer-stripes)" clipPath="url(#br-clip)" />
  </svg>
);

/* Proper Dark Blue Certificate Border Frame (Clean, No Corner Squares) */
const ProperCertificateBorder = () => (
  <svg
    className="absolute inset-0 w-full h-full pointer-events-none select-none"
    viewBox="0 0 794 1123"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    {/* Outer Primary Dark Blue Border */}
    <rect
      x="22"
      y="22"
      width="750"
      height="1079"
      fill="none"
      stroke="#0f2b54"
      strokeWidth="2.5"
    />

    {/* Inner Secondary Dark Blue Fine Line */}
    <rect
      x="28"
      y="28"
      width="738"
      height="1067"
      fill="none"
      stroke="#183f7a"
      strokeWidth="1"
    />
  </svg>
);

const applyLetterVars = (text, vars) =>
  String(text || '').replace(/\{\{(\w+)\}\}/g, (_, key) => (vars[key] != null ? String(vars[key]) : ''));

const letterVars = (letter, signatory) => {
  const name = letter.intern_name || 'Candidate';
  const role = roleShort(letter.role_title);
  const start = prettyDate(letter.join_date);
  const end = prettyDate(letter.complete_date);
  const code = String(letter.letter_code || '').replace(/GMITS-/gi, 'GMIS-');
  return {
    candidate_name: name,
    internship_role: role,
    name,
    role,
    start_date: start,
    end_date: end,
    join_date: start,
    complete_date: end,
    issue_date: dmy(letter.issued_at || new Date().toISOString()),
    certificate_no: code,
    internship_id: code,
    duration: letter.duration || (letter.weeks_total ? `${letter.weeks_total} Weeks` : '4 Weeks'),
    authorized_person: signatory,
    college: letter.college || '',
    weeks: letter.weeks_total || ''
  };
};

/* ========================================================
   FORMAT 1: INTERNSHIP OFFER LETTER
   ======================================================== */
const OfferLetterDocument = ({ letter, blank = false }) => {
  const company = letter.company || {};
  const format = letter.format || {};
  const signatory = company.signatory || 'Vivek Kalaskar';
  const signTitle = company.signatory_title || 'Founder';

  const vars = letterVars(letter, signatory);

  const defaultBody = `Dear {{candidate_name}},\n\nWe are pleased to offer you an internship opportunity with **Growmore IT Services** for the role of **{{internship_role}} Intern**.\n\nThe internship will provide you with practical exposure through guided learning, assigned tasks, and project-based work related to your selected track.\n\n**Internship Details**\n\nRole: **{{internship_role}}**\nDuration: **{{duration}}**\nStart Date: **{{start_date}}**\nInternship ID: **{{internship_id}}**\n\nWe welcome you to **Growmore IT Services** and wish you a successful and rewarding learning experience.`;

  const body = applyLetterVars(fill(format.body, defaultBody), vars);
  const paragraphs = String(body).split(/\n+/).map((p) => p.trim()).filter(Boolean);

  return (
    <div className="cert-sheet mx-auto relative overflow-hidden bg-white print:shadow-none flex flex-col justify-between">
      {/* Proper Dark Blue Certificate Double-Border Frame */}
      <ProperCertificateBorder />

      {/* Offer Letter Content within Frame */}
      <div className="relative z-[1] px-16 pt-14 pb-8 flex-1 flex flex-col">
        {/* Top Header: Centered Logo and Full Divider Line */}
        <div>
          <div className="flex justify-center items-center">
            <GrowmoreLogo size="lg" />
          </div>

          {/* Full divider line under logo */}
          <div className="mt-3 mb-0 w-full h-[1.5px] bg-[#cbd5e1]" />
        </div>

        {/* Heading: INTERNSHIP OFFER LETTER */}
        <div className="text-center mt-7 mb-6">
          <h1 className="font-serif font-extrabold text-[22px] sm:text-[24px] tracking-[0.05em] uppercase text-[#0d3b66] leading-tight">
            INTERNSHIP OFFER LETTER
          </h1>
        </div>

        {/* Date & Candidate Recipient */}
        <div className="mb-4 text-[13.5px] font-sans text-[#1e293b]">
          <p className="mb-2">
            <span className="font-bold text-[#0f172a]">Date: </span>
            <span>{vars.issue_date}</span>
          </p>
          <p className="font-serif font-semibold text-[#0f172a] text-[14px]">To,</p>
          <p className="font-serif font-bold text-[15.5px] text-[#0f172a] leading-tight">
            {vars.candidate_name}
          </p>
          {letter.college && (
            <p className="text-[12px] text-[#64748b] font-sans mt-0.5">{letter.college}</p>
          )}
        </div>

        {/* Body Paragraphs with Internship Details */}
        <div className="space-y-3.5 text-[14px] sm:text-[14.5px] leading-[1.75] text-[#1e293b] font-serif text-justify mb-5">
          {paragraphs.map((para, i) => {
            if (
              para.startsWith('Role:') ||
              para.startsWith('Duration:') ||
              para.startsWith('Start Date:') ||
              para.startsWith('Internship ID:')
            ) {
              return (
                <div key={i} className="pl-4 py-0.5 text-[13.5px] font-sans">
                  <RichLine text={para} />
                </div>
              );
            }
            if (para === '**Internship Details**' || para === 'Internship Details') {
              return (
                <p key={i} className="font-sans font-bold text-[14px] text-[#0d3b66] pt-1">
                  Internship Details
                </p>
              );
            }
            return (
              <p key={i}>
                <RichLine text={para} />
              </p>
            );
          })}
        </div>

        {/* Date, Place, Sign-Off & Stamp Beside Signature (No QR Code Scanner) */}
        <div className="mt-3 mb-4">
          <div className="flex items-end justify-between">
            {/* Left: Signature & Stamp side by side */}
            <div className="flex items-end gap-6 sm:gap-8">
              {/* 1. Signatory Block */}
              <div className="min-w-[140px]">
                <p className="text-[13px] font-bold text-[#0f172a] font-sans mb-1">
                  For Growmore IT Services
                </p>
                <div className="h-12 flex items-end mb-1">
                  {blank ? (
                    <div className="h-12 w-36" />
                  ) : (
                    <SvgOrImage
                      content={format.signature_svg}
                      className="w-36 h-12"
                      fallback={<div className="h-12 w-36" />}
                    />
                  )}
                </div>
                <div className="w-[130px] border-b border-[#0f172a] mb-1.5" />
                <p className="font-bold text-[13px] text-[#0f172a] font-sans leading-tight">{signatory}</p>
                <p className="text-[11.5px] text-[#475569] font-sans leading-tight mt-0.5">{signTitle}</p>
                <p className="text-[11.5px] text-[#475569] font-sans leading-tight">Growmore IT Services</p>
              </div>

              {/* 2. Stamp beside the signature */}
              <div className="w-[96px] h-[96px] flex items-center justify-center">
                {blank ? (
                  <div className="w-[96px] h-[96px]" />
                ) : (
                  <SvgOrImage
                    content={format.stamp_svg}
                    className="w-[96px] h-[96px]"
                    fallback={<div className="w-[96px] h-[96px]" />}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Clean Footer Inside Frame, Comfortably Above the Bottom Border */}
        <div className="mt-auto pt-3 border-t border-[#e2e8f0] text-center mb-1">
          <div className="flex items-center justify-center gap-3 text-[12px] text-[#475569] font-sans">
            <div className="flex items-center gap-1.5">
              <GlobeIcon className="w-3.5 h-3.5 text-[#0d3b66]" />
              <span className="font-medium tracking-wide">growmoreitservices.com</span>
            </div>
            <span className="text-[#94a3b8]">|</span>
            <div className="flex items-center gap-1.5">
              <MailIcon className="w-3.5 h-3.5 text-[#0d3b66]" />
              <span className="font-medium tracking-wide">info@growmoreitservices.com</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ====================================================================
   FORMAT 2: INTERNSHIP COMPLETION CERTIFICATE / LETTER (Fresh & Proper)
   ==================================================================== */
const CompletionCertificateDocument = ({ letter, blank = false }) => {
  const company = letter.company || {};
  const format = letter.format || {};
  const signatory = company.signatory || 'Vivek Kalaskar';
  const signTitle = company.signatory_title || 'Founder';

  const vars = letterVars(letter, signatory);

  const defaultBody = `This is to certify that **{{candidate_name}}** has successfully completed an internship with **Growmore IT Services** as a **{{internship_role}} Intern** from **{{start_date}}** to **{{end_date}}**.\n\nDuring the internship, the candidate successfully completed assigned learning activities, practical tasks, and project-based work related to **{{internship_role}}**.\n\nWe appreciate the candidate's efforts and wish them success in their future academic and professional career.`;

  const body = applyLetterVars(fill(format.body, defaultBody), vars);
  const paragraphs = String(body).split(/\n+/).map((p) => p.trim()).filter(Boolean);

  const issueDateFormatted = vars.issue_date;
  const certNumber = String(letter.letter_code || vars.certificate_no || 'GMIS-CMP-DEMO').replace(/GMITS-/gi, 'GMIS-');

  const verifyUrl = letter.verify_url || `https://growmoreitservices.com/verify/${certNumber}`;
  const qrSrc = (!letter.qr_url || letter.qr_url === '/logo.png' || letter.qr_url.includes('logo'))
    ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(verifyUrl)}`
    : letter.qr_url;

  return (
    <div className="cert-sheet mx-auto relative overflow-hidden bg-white print:shadow-none flex flex-col justify-between">
      {/* Proper Dark Blue Certificate Double-Border Frame */}
      <ProperCertificateBorder />

      {/* Certificate Content within Frame (Spacious padding from border to logo) */}
      <div className="relative z-[1] px-16 pt-14 pb-8 flex-1 flex flex-col">
        {/* Top Header: Centered Logo and Full Divider Line */}
        <div>
          <div className="flex justify-center items-center">
            <GrowmoreLogo size="lg" />
          </div>

          {/* Full divider line under logo */}
          <div className="mt-3 mb-0 w-full h-[1.5px] bg-[#cbd5e1]" />
        </div>

        {/* Certificate Title (Spaced gracefully: doubled top space from line and increased bottom space) */}
        <div className="text-center mt-10 mb-8">
          <h1 className="font-serif font-extrabold text-[22px] sm:text-[24px] tracking-[0.05em] uppercase text-[#0d3b66] leading-tight">
            INTERNSHIP COMPLETION CERTIFICATE
          </h1>
        </div>

        {/* Certificate Meta: Certificate No. and Date Stacked Underneath */}
        <div className="mb-5 space-y-1 text-[13px] font-sans text-[#1e293b]">
          <p>
            <span className="font-bold text-[#0f172a]">Certificate No.: </span>
            <span className="font-mono font-semibold">{certNumber}</span>
          </p>
          <p>
            <span className="font-bold text-[#0f172a]">Date: </span>
            <span>{issueDateFormatted}</span>
          </p>
        </div>

        {/* Certificate Body Paragraphs */}
        <div className="space-y-4 text-[14px] sm:text-[14.5px] leading-[1.8] text-[#1e293b] font-serif text-justify mb-6">
          {paragraphs.map((para, i) => (
            <p key={i}>
              <RichLine text={para} />
            </p>
          ))}
        </div>

        {/* Date, Place, Sign-Off, Stamp Beside Signature, & Scanner at the Corner */}
        <div className="mt-3 mb-4">
          <div className="flex items-end justify-between">
            {/* Left: Signature & Stamp side by side */}
            <div className="flex items-end gap-6 sm:gap-8">
              {/* 1. Signatory Block */}
              <div className="min-w-[140px]">
                <p className="text-[13px] font-bold text-[#0f172a] font-sans mb-1">
                  For Growmore IT Services
                </p>
                <div className="h-12 flex items-end mb-1">
                  {blank ? (
                    <div className="h-12 w-36" />
                  ) : (
                    <SvgOrImage
                      content={format.signature_svg}
                      className="w-36 h-12"
                      fallback={<div className="h-12 w-36" />}
                    />
                  )}
                </div>
                <div className="w-[130px] border-b border-[#0f172a] mb-1.5" />
                <p className="font-bold text-[13px] text-[#0f172a] font-sans leading-tight">{signatory}</p>
                <p className="text-[11.5px] text-[#475569] font-sans leading-tight mt-0.5">{signTitle}</p>
                <p className="text-[11.5px] text-[#475569] font-sans leading-tight">Growmore IT Services</p>
              </div>

              {/* 2. Stamp beside the signature */}
              <div className="w-[96px] h-[96px] flex items-center justify-center">
                {blank ? (
                  <div className="w-[96px] h-[96px]" />
                ) : (
                  <SvgOrImage
                    content={format.stamp_svg}
                    className="w-[96px] h-[96px]"
                    fallback={<div className="w-[96px] h-[96px]" />}
                  />
                )}
              </div>
            </div>

            {/* Right: Real QR Code Scanner at the corner */}
            <div className="flex flex-col items-center select-none">
              <div className="p-1.5 bg-white border border-gray-300 rounded shadow-xs">
                <img
                  src={qrSrc}
                  alt="Scan to verify certificate"
                  className="w-[84px] h-[84px] object-contain"
                  draggable="false"
                />
              </div>
              <span className="text-[9.5px] font-sans font-semibold tracking-wider uppercase text-[#64748b] mt-1.5">
                Scan to Verify
              </span>
            </div>
          </div>
        </div>

        {/* Clean Footer Inside Frame, Comfortably Above the Bottom Border */}
        <div className="mt-auto pt-3 border-t border-[#e2e8f0] text-center mb-1">
          <div className="flex items-center justify-center gap-3 text-[12px] text-[#475569] font-sans">
            <div className="flex items-center gap-1.5">
              <GlobeIcon className="w-4 h-4 text-[#0f384c]" />
              <span className="font-medium">growmoreitservices.com</span>
            </div>
            <span className="text-[#cbd5e1]">|</span>
            <div className="flex items-center gap-1.5">
              <MailIcon className="w-4 h-4 text-[#0f384c]" />
              <span className="font-medium">{company.email || 'info@growmoreitservices.com'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* Master LetterDocument Component */
const LetterDocument = ({ letter, blank = false, unsigned = false }) => {
  if (!letter) return null;

  if (letter.type === 'offer') {
    return <OfferLetterDocument letter={letter} blank={blank || unsigned} />;
  }

  return <CompletionCertificateDocument letter={letter} blank={blank || unsigned} />;
};

export default LetterDocument;
